<template>
    <div class="mass-update-dashboard">
        <mass-stats
            :configurations-count="configurations.length"
            :finished-count="finishedCount"
            :labels="labels"
            :running-count="runningCount"
            :selected-count="selectedIds.length"/>

        <div class="row">
            <div class="col-lg-8">
                <mass-global-config
                    :branches="branches"
                    :branches-loading="branchesLoading"
                    :global-config="globalConfig"
                    :include-publishing="includePublishing"
                    :is-starting-run="isStartingRun"
                    :labels="labels"
                    :node-versions="nodeVersions"
                    :platforms="platforms"
                    :repositories="repositories"
                    :start-disabled="startDisabled"
                    :start-feedback-message="startFeedbackMessage"
                    @start-builds="startBuilds"
                    @update-global-config="updateGlobalConfig"
                    @update-include-publishing="includePublishing = $event"
                    @update-platform="updatePlatform"
                    @refresh-branches="fetchBranches(globalConfig.source_repository_id, true)"/>

                <mass-selection-queue
                    :configurations="configurations"
                    :labels="labels"
                    :selected-ids="selectedIds"
                    @remove="removeFromSelection"
                    @update-selected-ids="selectedIds = $event"/>

                <mass-configuration-table
                    :all-selected="allSelected"
                    :current-page="currentPage"
                    :error-message="errorMessage"
                    :filtered-configurations="filteredConfigurations"
                    :labels="labels"
                    :loading="loading"
                    :log-open="logOpen"
                    :pages-to-show="pagesToShow"
                    :paginated-configurations="paginatedConfigurations"
                    :per-page="perPage"
                    :poll-timer="pollTimer"
                    :search-query="searchQuery"
                    :selected-ids="selectedIds"
                    :total-pages="totalPages"
                    @toggle-log="toggleLog"
                    @toggle-select-all="toggleSelectAll"
                    @update-current-page="currentPage = $event"
                    @update-search="searchQuery = $event"
                    @update-selected-ids="selectedIds = $event"/>
            </div>

            <div class="col-lg-4 mass-update-sidebar">
                <div ref="runStack">
                    <mass-run-stack
                        :branches="branches"
                        :configurations="configurations"
                        :just-started-run-id="justStartedRunId"
                        :labels="labels"
                        :node-versions="nodeVersions"
                        :open-runs="openRuns"
                        :repositories="repositories"
                        :runs="runs"
                        @stop-current-run="stopCurrentRun"
                        @toggle-run="toggleRun"/>
                </div>

                <mass-latest-run-summary
                    :failed-count="currentRunFailedCount"
                    :failed-percent="currentRunFailedPercent"
                    :finished-count="currentRunFinishedCount"
                    :finished-percent="currentRunFinishedPercent"
                    :has-run="runs.length > 0 && !!runs[0]"
                    :overall-percent="currentRunOverallPercent"
                    :poll-interval-ms="pollIntervalMs"
                    :poll-timer="pollTimer"
                    :running-count="currentRunRunningCount"
                    :running-percent="currentRunRunningPercent"
                    :total-builds="currentRunTotalBuilds"/>

                <mass-failed-builds
                    :failed-builds-across-runs="failedBuildsAcrossRuns"
                    :failed-builds-page="failedBuildsPage"
                    :failed-total-pages="failedTotalPages"
                    :paginated-failed-builds="paginatedFailedBuilds"
                    :per-page-failed="perPageFailed"
                    @update-failed-builds-page="failedBuildsPage = $event"/>
            </div>
        </div>
    </div>
</template>

<script>
import * as buildStates from './mass_update/buildStates';
import * as massUpdateApi from './mass_update/massUpdateApi';
import MassConfigurationTable from './mass_update/MassConfigurationTable.vue';
import MassFailedBuilds from './mass_update/MassFailedBuilds.vue';
import MassGlobalConfig from './mass_update/MassGlobalConfig.vue';
import MassLatestRunSummary from './mass_update/MassLatestRunSummary.vue';
import MassRunStack from './mass_update/MassRunStack.vue';
import MassSelectionQueue from './mass_update/MassSelectionQueue.vue';
import MassStats from './mass_update/MassStats.vue';

export default {
    components: {
        MassConfigurationTable,
        MassFailedBuilds,
        MassGlobalConfig,
        MassLatestRunSummary,
        MassRunStack,
        MassSelectionQueue,
        MassStats,
    },
    data() {
        return {
            runId: null,
            run: null,
            configurations: [],
            loading: true,
            selectedIds: [],
            platforms: {
                ios: true,
                android: true,
            },
            includePublishing: false,
            nodeVersions: [],
            repositories: [],
            branches: [],
            branchesLoading: false,
            globalConfig: {
                node_version_id: '',
                java_version: '',
                source_repository_id: '',
                source_branch_id: '',
                ios_version: '',
                android_version: '',
                android_version_code: '',
                ios_publishing_type: '',
                android_publishing_type: '',
                whats_new: '',
            },
            errorMessage: '',
            isStartingRun: false,
            justStartedRunId: null,
            startFeedbackMessage: '',
            startFeedbackTimer: null,
            runs: [],
            openRuns: {},
            pollTimer: null,
            pollIntervalMs: 5000,
            logOpen: {},
            searchQuery: '',
            currentPage: 1,
            perPage: 15,
            failedBuildsPage: 1,
            perPageFailed: 5,
            labels: {
                massUpdate: 'Massenupdate',
                livePolling: 'Live-Abfrage',
                loading: 'Konfigurationen werden geladen...',
                total: 'Gesamt',
                selected: 'Ausgewählt',
                running: 'Wird ausgeführt',
                finished: 'Abgeschlossen',
                failed: 'Fehlgeschlagen',
                configurations: 'Konfigurationen',
                totalConfigurations: 'Gesamtkonfigurationen',
                selectedConfigurations: 'Ausgewählte Konfigurationen',
                inProgress: 'Builds werden ausgeführt',
                finishedBuilds: 'Abgeschlossene Builds',
                includePublishing: 'Veröffentlichung einbeziehen',
                startBuilds: 'Massenupdate starten',
                startingRun: 'Massenupdate wird gestartet...',
                runStarted: 'Neues Massenupdate wurde gestartet.',
                appName: 'App-Name',
                client: 'Client',
                id: 'ID',
                internalName: 'Interner Name',
                updatedAt: 'Aktualisiert',
                bundleId: 'Bundle-ID',
                repository: 'Repository',
                status: 'Status',
                actions: 'Aktionen',
                showLog: 'Log anzeigen',
                hideLog: 'Log ausblenden',
                noLog: 'Noch kein Log verfügbar.',
                noBuild: 'Noch keine Builds',
                searchPlaceholder: 'Nach App, Client, Bundle oder Repository suchen...',
                noResults: 'Keine Konfigurationen gefunden, die Ihrer Suche entsprechen.',
                globalConfig: 'Globale Konfiguration für dieses Massenupdate',
                nodeVersion: 'Node-Version',
                javaVersion: 'Java-Version',
                sourceRepository: 'Quell-Repository',
                sourceBranch: 'Quell-Branch',
                iosVersion: 'iOS-Version',
                androidVersion: 'Android-Version',
                androidVersionCode: 'Android-Version-Code',
                whatsNew: 'Neues in dieser Version',
                defaultOption: 'Standard (aus App-Konfig)',
                selectedOrder: 'Reihenfolge der ausgewählten Apps (Drag & Drop zum Sortieren)',
                noAppsSelected: 'Keine Apps ausgewählt. Wählen Sie Apps aus der Liste unten aus.',
                showing: 'Zeige',
                of: 'von',
                entries: 'Einträgen',
            },
        };
    },
    computed: {
        allSelected() {
            return this.configurations.length > 0 && this.selectedIds.length === this.configurations.length;
        },
        startDisabled() {
            return this.selectedIds.length === 0 || (!this.platforms.ios && !this.platforms.android);
        },
        filteredConfigurations() {
            const query = this.searchQuery.trim().toLowerCase();
            if (!query) {
                return this.configurations;
            }
            return this.configurations.filter((config) => {
                const haystack = [
                    config.app_name,
                    config.internal_name,
                    config.client_name,
                    config.bundle_id,
                    config.target_repository,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(query);
            });
        },
        runningCount() {
            return this.configurations.filter((config) => {
                const build = this.currentBuild(config);
                return build && this.isRunning(build.state);
            }).length;
        },
        finishedCount() {
            return this.configurations.filter((config) => {
                const build = this.currentBuild(config);
                return build && this.isFinished(build.state);
            }).length;
        },
        // --- Current run stats (scoped to runs[0].builds, not the global config list) ---
        currentRunBuilds() {
            return (this.runs.length > 0 && this.runs[0].builds) ? this.runs[0].builds : [];
        },
        currentRunTotalBuilds() {
            return this.currentRunBuilds.length;
        },
        currentRunFinishedCount() {
            return this.currentRunBuilds.filter(b => this.isFinished(b.state)).length;
        },
        currentRunRunningCount() {
            return this.currentRunBuilds.filter(b => this.isRunning(b.state)).length;
        },
        currentRunFailedCount() {
            return this.currentRunBuilds.filter(b => this.isFailed(b.state) || this.isCanceled(b.state)).length;
        },
        currentRunFinishedPercent() {
            if (!this.currentRunTotalBuilds) return 0;
            return Math.round((this.currentRunFinishedCount / this.currentRunTotalBuilds) * 100);
        },
        currentRunRunningPercent() {
            if (!this.currentRunTotalBuilds) return 0;
            return Math.round((this.currentRunRunningCount / this.currentRunTotalBuilds) * 100);
        },
        currentRunFailedPercent() {
            if (!this.currentRunTotalBuilds) return 0;
            return Math.round((this.currentRunFailedCount / this.currentRunTotalBuilds) * 100);
        },
        currentRunOverallPercent() {
            if (!this.currentRunTotalBuilds) return 0;
            const done = this.currentRunFinishedCount + this.currentRunFailedCount;
            return Math.round((done / this.currentRunTotalBuilds) * 100);
        },
        // -------------------------------------------------------------------------------
        failedBuildsAcrossRuns() {
            const failed = [];
            this.runs.forEach((runData) => {
                if (!runData.builds) return;
                runData.builds.forEach((build) => {
                    if (!this.isFailed(build.state) && !this.isCanceled(build.state)) return;
                    const failedAtSync = this.isFailed(build.state) && !this.hasSyncCompleted(build);
                    failed.push({
                        buildId: build.id,
                        appName: this.getAppName(build.app_configuration_id),
                        failedAtSync,
                        codemagicAppId: build.codemagic_app_id || null,
                        codemagicBuildId: build.codemagic_build_id || null,
                        runDate: runData.run.started_at ? this.formatDate(runData.run.started_at) : '-',
                    });
                });
            });
            return failed;
        },
        paginatedFailedBuilds() {
            const start = (this.failedBuildsPage - 1) * this.perPageFailed;
            return this.failedBuildsAcrossRuns.slice(start, start + this.perPageFailed);
        },
        failedTotalPages() {
            return Math.max(1, Math.ceil(this.failedBuildsAcrossRuns.length / this.perPageFailed));
        },
        paginatedConfigurations() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredConfigurations.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredConfigurations.length / this.perPage);
        },
        pagesToShow() {
            const range = [];
            for (let i = 1; i <= this.totalPages; i++) {
                if (i === 1 || i === this.totalPages || Math.abs(i - this.currentPage) <= 2) {
                    range.push(i);
                }
            }
            return range;
        },
    },
    watch: {
        searchQuery() {
            this.currentPage = 1;
        },
        failedTotalPages(newVal) {
            if (this.failedBuildsPage > newVal) {
                this.failedBuildsPage = newVal;
            }
        },
        'globalConfig.source_repository_id': function (newVal) {
            this.fetchBranches(newVal);
            this.globalConfig.source_branch_id = '';
        }
    },
    created() {
        if (window.Laravel && window.Laravel.csrfToken) {
            window.axios.defaults.headers.common['X-CSRF-TOKEN'] = window.Laravel.csrfToken;
        }
        // fetch configurations (metadata) first and then current run
        this.fetchConfigurations().then(() => this.fetchCurrentRun());
    },
    beforeDestroy() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
        if (this.startFeedbackTimer) {
            clearTimeout(this.startFeedbackTimer);
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}.${month}.${year} ${hours}:${minutes}`;
        },
        getAppName(configId) {
            const config = this.configurations.find(c => c.id === configId);
            return config ? config.app_name : 'Unbekannt';
        },
        fetchBranches(repoId, forceRefresh = false) {
            this.branches = [];
            if (!repoId) {
                return;
            }

            this.branchesLoading = true;
            massUpdateApi.fetchBranches(repoId, forceRefresh)
                .then(response => {
                    this.branches = response.data;
                })
                .catch(error => {
                    console.error('Failed to fetch branches:', error);
                })
                .finally(() => {
                    this.branchesLoading = false;
                });
        },
        fetchConfigurations() {
            this.loading = true;
            return massUpdateApi.fetchConfigurations()
                .then((response) => {
                    this.configurations = response.data.configurations.map((config) => {
                        if (config.last_build) {
                            config.current_build_id = config.last_build.id;
                            config.current_build = config.last_build;
                        } else {
                            config.current_build_id = null;
                            config.current_build = null;
                        }
                        return config;
                    });
                    this.nodeVersions = response.data.node_versions || [];
                    this.repositories = response.data.repositories || [];
                    this.loading = false;
                    this.startPolling();
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || error.message;
                    this.loading = false;
                });
        },
        toggleSelectAll(event) {
            if (event.target.checked) {
                this.selectedIds = this.configurations.map((config) => config.id);
            } else {
                this.selectedIds = [];
            }
        },
        updateGlobalConfig({key, value}) {
            this.$set(this.globalConfig, key, value);
        },
        updatePlatform({platform, value}) {
            this.$set(this.platforms, platform, value);
        },
        startBuilds() {
            if (this.isStartingRun) {
                return;
            }

            this.errorMessage = '';
            this.startFeedbackMessage = '';
            if (this.selectedIds.length === 0) {
                this.errorMessage = 'Bitte wählen Sie mindestens eine Konfiguration aus.';
                return;
            }
            if (!this.platforms.ios && !this.platforms.android) {
                this.errorMessage = 'Bitte wählen Sie mindestens eine Plattform aus.';
                return;
            }

            const platforms = [];
            if (this.platforms.ios) platforms.push('ios');
            if (this.platforms.android) platforms.push('android');

            const globalConfig = Object.assign({}, this.globalConfig, {
                android_version_code: this.globalConfig.android_version_code === null || this.globalConfig.android_version_code === undefined
                    ? ''
                    : String(this.globalConfig.android_version_code),
            });

            this.isStartingRun = true;

            massUpdateApi.startRun({
                configuration_ids: this.selectedIds,
                platforms,
                include_publishing: this.includePublishing,
                ...globalConfig,
            }).then((response) => {
                const builds = response.data.builds || [];
                const runId = response.data.mass_update_run_id || null;
                if (runId) {
                    this.runId = runId;
                    this.run = {
                        id: runId,
                        status: 'running',
                        builds: builds,
                        total_builds: builds.length,
                        completed_builds: 0,
                        started_at: new Date().toISOString(),
                        platforms: platforms,
                        include_publishing: this.includePublishing,
                        ...globalConfig,
                    };

                    // Add to runs list and open it
                    this.justStartedRunId = runId;
                    this.runs.unshift({
                        run: this.run,
                        builds: builds
                    });
                    this.$set(this.openRuns, runId, true);

                    // Keep only last 5
                    if (this.runs.length > 5) {
                        this.runs = this.runs.slice(0, 5);
                    }

                    this.showRunStartedFeedback();
                }
                builds.forEach((build) => {
                    const config = this.configurations.find((c) => c.id === build.app_configuration_id);
                    if (config) {
                        config.current_build_id = build.id;
                        config.current_build = build;
                    }
                });
                this.startPolling();
            }).catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            }).finally(() => {
                this.isStartingRun = false;
            });
        },
        showRunStartedFeedback() {
            this.startFeedbackMessage = this.labels.runStarted;

            if (this.startFeedbackTimer) {
                clearTimeout(this.startFeedbackTimer);
            }

            this.$nextTick(() => {
                const target = this.$refs.runStack;
                if (target && typeof target.scrollIntoView === 'function') {
                    target.scrollIntoView({behavior: 'smooth', block: 'start'});
                }
            });

            this.startFeedbackTimer = setTimeout(() => {
                this.startFeedbackMessage = '';
                this.justStartedRunId = null;
                this.startFeedbackTimer = null;
            }, 5000);
        },
        startPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
            }
            this.pollTimer = setInterval(this.pollStatuses, this.pollIntervalMs);
            this.pollStatuses();
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        removeFromSelection(id) {
            this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id);
        },

        fetchCurrentRun() {
            return massUpdateApi.fetchCurrentRun()
                .then((response) => {
                    this.runs = response.data.runs || [];
                    if (this.runs.length > 0) {
                        const latest = this.runs[0];
                        const run = latest.run;
                        run.builds = latest.builds;
                        this.run = run;
                        this.runId = this.run.id;

                        // Open the latest run by default if none are open
                        if (Object.keys(this.openRuns).length === 0) {
                            this.$set(this.openRuns, this.run.id, true);
                        }

                        // Populate global config from current/last run
                        this.globalConfig.node_version_id = this.run.node_version_id || '';
                        this.globalConfig.java_version = this.run.java_version || '';
                        this.globalConfig.source_repository_id = this.run.source_repository_id || '';
                        this.$nextTick(() => {
                            this.globalConfig.source_branch_id = this.run.source_branch_id || '';
                        });
                        this.globalConfig.ios_version = this.run.ios_version || '';
                        this.globalConfig.android_version = this.run.android_version || '';
                        this.globalConfig.android_version_code = this.run.android_version_code || '';
                        this.globalConfig.ios_publishing_type = this.run.ios_publishing_type || '';
                        this.globalConfig.android_publishing_type = this.run.android_publishing_type || '';
                        this.globalConfig.whats_new = this.run.whats_new || '';

                        this.platforms.ios = this.run.platforms ? this.run.platforms.includes('ios') : true;
                        this.platforms.android = this.run.platforms ? this.run.platforms.includes('android') : true;
                        this.includePublishing = !!this.run.include_publishing;
                    }
                }).catch(() => {
                    this.runs = [];
                    this.run = null;
                    this.runId = null;
                });
        },
        pollStatuses() {
            // If runId is present poll run-specific endpoint to receive aggregated data
            if (this.runId) {
                massUpdateApi.pollRunStatus(this.runId)
                    .then((response) => {
                        const run = this.applyRunStatusResponse(response);

                        // Stop polling if run is no longer running
                        if (run && run.status !== 'running') {
                            this.stopPolling();
                        }
                    }).catch((error) => {
                    this.errorMessage = error.response?.data?.message || error.message;
                });
                return;
            }

            const buildIds = this.configurations
                .map((config) => config.current_build_id)
                .filter((id) => !!id);

            if (buildIds.length === 0) {
                return;
            }

            massUpdateApi.pollBuildStatuses(buildIds).then((response) => {
                const builds = response.data.builds || [];
                builds.forEach((build) => {
                    const config = this.configurations.find((c) => c.id === build.app_configuration_id);
                    if (config) {
                        config.current_build = build;
                    }
                });
            }).catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            });
        },
        currentBuild(config) {
            return config.current_build || null;
        },
        isRunning(state) {
            return buildStates.isRunning(state);
        },
        isFinished(state) {
            return buildStates.isFinished(state);
        },
        isCanceled(state) {
            return buildStates.isCanceled(state);
        },
        isFailed(state) {
            return buildStates.isFailed(state);
        },
        hasSyncCompleted(build) {
            return buildStates.hasSyncCompleted(build);
        },
        toggleLog(config) {
            this.$set(this.logOpen, config.id, !this.showLog(config));
        },
        showLog(config) {
            return !!this.logOpen[config.id];
        },
        applyRunStatusResponse(response) {
            const run = response.data.run || null;
            if (run && response.data.builds) {
                run.builds = response.data.builds;

                // Update the run in our runs array
                const runIndex = this.runs.findIndex(r => r.run.id == run.id);
                if (runIndex !== -1) {
                    this.$set(this.runs[runIndex], 'run', run);
                    this.$set(this.runs[runIndex], 'builds', response.data.builds);
                } else {
                    this.runs.unshift({run: run, builds: response.data.builds});
                }
            }

            if (run && (!this.run || this.run.id == run.id || this.runId == run.id)) {
                this.run = run;
            }

            const builds = response.data.builds || [];
            builds.forEach((rb) => {
                // find configuration by app_configuration_id and update current_build if matches
                const config = this.configurations.find((c) => c.id === rb.app_configuration_id);
                if (config) {
                    config.current_build_id = rb.build_id;
                    config.current_build = Object.assign({}, config.current_build || {}, {
                        id: rb.build_id,
                        app_configuration_id: rb.app_configuration_id,
                        state: rb.state,
                        pivot_state: rb.pivot_state,
                        state_text: rb.state_text || config.current_build?.state_text,
                        codemagic_build_id: rb.codemagic_build_id,
                        codemagic_app_id: rb.codemagic_app_id,
                        branch_to_build: rb.branch_to_build,
                        started_at: rb.started_at,
                        finished_at: rb.finished_at,
                        log: rb.log,
                    });
                }
            });

            return run;
        },
        stopCurrentRun(runId = this.runId) {
            if (!runId) {
                this.errorMessage = 'Kein aktives Massenupdate zum Stoppen vorhanden.';
                return;
            }

            if (!confirm('Möchten Sie das Massenupdate wirklich stoppen? Alle in der Warteschlange befindlichen und laufenden Builds werden abgebrochen.')) {
                return;
            }

            massUpdateApi.stopRun(runId)
                .then((response) => {
                    const stoppedRun = response.data.run || {};
                    const stoppedRunId = stoppedRun.id || runId;
                    const runIndex = this.runs.findIndex(r => r.run.id == stoppedRunId);

                    if (runIndex !== -1) {
                        const run = Object.assign({}, this.runs[runIndex].run, {
                            status: stoppedRun.status || 'stopped',
                            finished_at: stoppedRun.finished_at || this.runs[runIndex].run.finished_at,
                        });
                        this.$set(this.runs[runIndex], 'run', run);
                    }

                    if (this.run && this.run.id == stoppedRunId) {
                        this.run.status = stoppedRun.status || 'stopped';
                        this.run.finished_at = stoppedRun.finished_at || this.run.finished_at;
                    }

                    return massUpdateApi.pollRunStatus(stoppedRunId)
                        .then((statusResponse) => this.applyRunStatusResponse(statusResponse))
                        .finally(() => {
                            if (this.runId == stoppedRunId) {
                                this.stopPolling();
                            }
                        });
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || 'Massenupdate konnte nicht gestoppt werden: ' + error.message;
                });
        },
        toggleRun(runId) {
            this.$set(this.openRuns, runId, !this.openRuns[runId]);
        },
    },
};
</script>

<style src="./mass_update/MassUpdate.css"></style>
